import {
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  BriefcaseBusiness,
  CalendarClock,
  ClipboardList,
  DollarSign,
  FileText,
  Handshake,
  Layers,
  Mail,
  Network,
  Newspaper,
  Repeat,
  Settings2,
  Shield,
  SlidersHorizontal,
  Ticket,
  TrendingUp,
  Trophy,
  UserCog,
  Wallet,
  Workflow,
  type LucideIcon,
} from 'lucide-react'

import type { SessionGetCreateResponse } from '@api/orval/apiHooks'
import { crudKey, hasPermission, type PermissionResource } from '@features/permissions/permissions'
import { isRouteEnabledForProject, redirectsList } from '@lib/constants'
import { PAYOUTS_LABEL, PAYOUTS_ROUTE } from '@lib/payoutTerminology'

export type NavGroupName = 'Finance' | 'Growth' | 'Operations' | 'Insights' | 'Configuration'

export type NavItem = {
  title: string
  href: string
  /** Backend permission resource(s) behind the page; the link shows with `<resource>.read` on any of them. */
  resource: PermissionResource | PermissionResource[]
  /** Rendered beside the title in grouped dropdowns. Omitted for always-visible links, which stay text-only. */
  icon?: LucideIcon
  /** Absent for the handful of links that stay ungrouped in the bar. */
  group?: NavGroupName
  /** Separator line below this item in the group dropdown; dropped when it would be the last line. */
  dividerAfter?: boolean
}

export const NAV_ITEMS: NavItem[] = [
  { title: 'Accounts', href: '/accounts', resource: 'trading_accounts' },
  { title: 'Users', href: '/users', resource: 'users' },
  {
    title: 'Referrals',
    href: '/referrals',
    resource: 'referrals',
    group: 'Growth',
    icon: Handshake,
  },
  {
    title: 'Deposits',
    href: '/deposits',
    resource: 'deposits',
    group: 'Finance',
    icon: ArrowDownToLine,
  },

  {
    title: 'Internal Transfers',
    href: '/internal_transfers',
    resource: 'internal_transfers',
    group: 'Finance',
    icon: Repeat,
  },

  {
    title: 'Withdrawals',
    href: '/withdrawals',
    resource: 'withdrawals',
    group: 'Finance',
    icon: ArrowUpFromLine,
  },
  {
    title: PAYOUTS_LABEL,
    href: PAYOUTS_ROUTE,
    resource: 'payouts',
    group: 'Finance',
    icon: DollarSign,
    dividerAfter: true,
  },
  {
    title: 'Transactions',
    href: '/transactions',
    resource: 'transactions',
    group: 'Finance',
    icon: ArrowLeftRight,
  },
  {
    title: 'Email Templates',
    href: '/email_templates',
    resource: 'email_templates',
    group: 'Operations',
    icon: Mail,
  },
  { title: 'Coupons', href: '/coupons', resource: 'coupons', group: 'Growth', icon: Ticket },
  {
    title: 'Tasks',
    href: '/tasks',
    resource: 'tasks',
    group: 'Operations',
    icon: ClipboardList,
  },
  {
    title: 'Scheduled Actions',
    href: '/scheduled_actions',
    resource: 'scheduled_actions',
    group: 'Operations',
    icon: CalendarClock,
  },
  {
    title: 'News Items',
    href: '/news_items',
    resource: 'news_items',
    group: 'Operations',
    icon: Newspaper,
  },
  {
    title: 'Discount Tiers',
    href: '/discount_tiers',
    resource: 'discount_tiers',
    group: 'Growth',
    icon: Layers,
  },
  { title: 'IB Profiles', href: '/ib_profiles', resource: 'ib_profiles', group: 'Growth', icon: BriefcaseBusiness },
  {
    title: 'Account Templates',
    href: '/templates',
    resource: 'templates',
    group: 'Configuration',
    icon: FileText,
  },
  {
    title: 'Account Groups',
    href: '/groups',
    resource: 'groups',
    group: 'Configuration',
    icon: Network,
  },
  {
    title: 'Competitions',
    href: '/competitions',
    resource: 'competitions',
    group: 'Growth',
    icon: Trophy,
  },
  {
    title: 'Permission Groups',
    href: '/permission_groups',
    resource: 'permission_groups',
    group: 'Configuration',
    icon: Shield,
  },
  {
    title: 'App Settings',
    href: '/app-settings',
    resource: ['app_settings', 'brands'],
    group: 'Configuration',
    icon: Settings2,
  },
  {
    title: 'Sales Agents',
    href: '/sales_agents',
    resource: 'sales_agents',
    group: 'Configuration',
    icon: UserCog,
  },
]

// Bar order, cold config last — it is also the first group the overflow menu swallows.
const GROUPS: { title: NavGroupName; icon: LucideIcon }[] = [
  { title: 'Finance', icon: Wallet },
  { title: 'Growth', icon: TrendingUp },
  { title: 'Operations', icon: Workflow },
  { title: 'Configuration', icon: SlidersHorizontal },
]

const enabledItems = NAV_ITEMS.filter((item) => isRouteEnabledForProject(item.href))

type NavUser = Pick<SessionGetCreateResponse, 'super_user' | 'keys'> | null | undefined

const canRead = (user: NavUser, { resource }: NavItem) =>
  [resource].flat().some((r) => hasPermission(user, crudKey(r, 'read')))

const readableItems = (user: NavUser) => enabledItems.filter((item) => canRead(user, item))

/**
 * Page-level guard: a route (or any sub-route, e.g. `/users/42`) behind a nav item needs that item's `.read`.
 * Hiding the link isn't enough — a typed or shared URL would otherwise render a silently empty page, since 403s
 * on background queries don't toast. Routes outside the nav are not gated here.
 */
export const canReadRoute = (user: NavUser, pathname: string): boolean => {
  const item = NAV_ITEMS.find(({ href }) => pathname === href || pathname.startsWith(`${href}/`))

  return !item || canRead(user, item)
}

export const hasDividerAfter = (items: NavItem[], index: number) =>
  !!items[index]?.dividerAfter && index < items.length - 1

export type NavSlot =
  { kind: 'link'; item: NavItem } | { kind: 'group'; title: NavGroupName; icon: LucideIcon; items: NavItem[] }

// Keep old-CRM links below the in-app pages they sit next to in a group.
const oldCrmLast = (a: NavItem, b: NavItem) =>
  Number(redirectsList.includes(a.href)) - Number(redirectsList.includes(b.href))

const toNavSlots = (items: NavItem[]): NavSlot[] => {
  const directSlots: NavSlot[] = items.filter((item) => !item.group).map((item) => ({ kind: 'link' as const, item }))

  // A group left with one route (brand filter or permissions) would render as a one-item dropdown — flatten it instead.
  const groupSlots: NavSlot[] = GROUPS.map(({ title, icon }) => {
    const groupItems = items.filter((item) => item.group === title).sort(oldCrmLast)

    return groupItems.length === 1
      ? { kind: 'link' as const, item: groupItems[0]! }
      : { kind: 'group' as const, title, icon, items: groupItems }
  }).filter((slot) => slot.kind === 'link' || slot.items.length > 0)

  return [...directSlots, ...groupSlots]
}

export const navSlots: NavSlot[] = toNavSlots(enabledItems)

export const getNavSlotsForUser = (user: NavUser): NavSlot[] => toNavSlots(readableItems(user))
